import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X, FileEarmarkPdf } from 'react-bootstrap-icons';
import SectionSelector from '../SectionSelector';
import type { ReportSection } from '../SectionSelector/SectionSelector';
import './ExportConfigModal.scss';

// ============================================
// Component Props
// ============================================

interface ExportConfigModalProps {
  show: boolean;
  onHide: () => void;
  onConfirmExport: (selectedSections: Set<string>) => void;
  availableSections: ReportSection[];
  isExporting?: boolean;
}

// ============================================
// Main Component
// ============================================

const ExportConfigModal: React.FC<ExportConfigModalProps> = ({
  show,
  onHide,
  onConfirmExport,
  availableSections,
  isExporting = false
}) => {
  // Initialize with all sections selected
  const [selectedSections, setSelectedSections] = useState<Set<string>>(() => {
    return new Set(availableSections.map(s => s.id));
  });

  // Reset selections when modal opens
  React.useEffect(() => {
    if (show) {
      setSelectedSections(new Set(availableSections.map(s => s.id)));
    }
  }, [show, availableSections]);

  // ============================================
  // Section Selection Handlers
  // ============================================

  const handleToggleSection = (sectionId: string) => {
    setSelectedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    const allSelectableIds = availableSections
      .filter(s => !s.required)
      .map(s => s.id);
    
    const allSelected = allSelectableIds.every(id => selectedSections.has(id));
    
    setSelectedSections(prev => {
      const newSet = new Set(prev);
      if (allSelected) {
        // Deselect all selectable sections
        allSelectableIds.forEach(id => newSet.delete(id));
      } else {
        // Select all sections
        allSelectableIds.forEach(id => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleConfirm = () => {
    onConfirmExport(selectedSections);
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="export-config-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Configurar Exportación
          </Modal.Title>
          <p className="form-modal-subtitle">
            Selecciona las secciones que deseas incluir en el PDF
          </p>
        </div>
        <Button variant="none" onClick={onHide} className="modal-close-btn" disabled={isExporting}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        <SectionSelector
          sections={availableSections}
          selectedSections={selectedSections}
          onToggleSection={handleToggleSection}
          onToggleAll={handleToggleAll}
        />
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={onHide}
            disabled={isExporting}
          >
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-confirm"
            onClick={handleConfirm}
            disabled={isExporting || selectedSections.size === 0}
          >
            {isExporting ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Exportando...</span>
                </div>
                Exportando...
              </>
            ) : (
              <>
                <FileEarmarkPdf className="me-2" />
                Generar PDF
                {selectedSections.size > 0 && (
                  <span className="section-count-badge">
                    {selectedSections.size}
                  </span>
                )}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportConfigModal;
